import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { Helmet } from "react-helmet";
import * as Yup from "yup";

function encode(data) {
  return Object.keys(data)
    .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&");
}

function WorkWithUs() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const phoneRegExp = /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

  const validationSchema = Yup.object().shape({
    full_name: Yup.string().required("Please enter your full name"),
    email: Yup.string()
      .email("Please enter a valid email address")
      .required("Please enter your email address"),
    mobilephone: Yup.string().matches(
      phoneRegExp,
      "Please enter a valid phone number"
    ),
    need: Yup.string().required("Please select what you need help with"),
    message: Yup.string().required("Please tell us what is happening"),
  });

  useEffect(() => {
    const navbar = document.getElementById("navbar");

    const handleScroll = () => {
      navbar?.classList.toggle("scrolled", window.scrollY > 40);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const onSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);

    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({
          "form-name": "work-with-us",
          ...values,
        }),
      });

      if (!response.ok) {
        throw new Error("The form could not be submitted.");
      }

      window.dataLayer = window.dataLayer || [];

      window.dataLayer.push({
        event: "GaForm",
        EventCategory: "form",
        EventAction: "submit",
        EventLabel: "Work with us",
        form_id: "work-with-us",
      });

      setFormSubmitted(true);
    } catch (error) {
      formik.setErrors({
        api: "There was an error with your form submission. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      "bot-field": "",
      full_name: "",
      email: "",
      mobilephone: "",
      need: "Website maintenance and support",
      message: "",
    },
    validationSchema,
    onSubmit,
  });

  return (
    <>
      <Helmet>
        <html lang="en-AU" />
        <title>Work With Us | Diviv</title>
        <meta
          name="description"
          content="Book a free website support review with Diviv. Perth-based web development, CMS support, application development and website maintenance specialists."
        />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Work+Sans:wght@400;500;600;700;800&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,300&display=swap"
          rel="stylesheet"
        />
        <style>{`
:root {
      --ink: #0d1c27;
      --muted: #5a6a77;
      --line: #e0e8ef;
      --soft: #f4f7f9;
      --paper: #ffffff;
      --brand: #0a7f91;
      --brand-dark: #075f6c;
      --brand-light: #d0f0f4;


      --success: #1a8c5a;
      --navy: #0d1f2d;
      --navy-mid: #152636;
      --radius: 10px;
      --radius-lg: 18px;
      --shadow-sm: 0 2px 10px rgba(10, 30, 50, 0.07);
      --shadow: 0 12px 40px rgba(10, 30, 50, 0.12);
      --shadow-lg: 0 24px 64px rgba(10, 30, 50, 0.18);
      font-family: 'DM Sans', sans-serif;
    }

    *,
    *::before,
    *::after {
      box-sizing: border-box;
      margin: 0;
    }

    html {
      scroll-behavior: smooth;
    }

    body {
      color: var(--ink);
      background: var(--paper);
      line-height: 1.6;
      font-family: 'DM Sans', sans-serif;
      -webkit-font-smoothing: antialiased;
      overflow-x: hidden;
    }

    a {
      color: inherit;
      text-decoration: none;
    }

    a:hover {
      text-decoration:none;
    }

    h1, h2, h3, h4, h5 {
    color: var(--ink);
    text-transform:none;
    }

    .topbar {
      background: var(--navy);
      color: rgba(255, 255, 255, 0.75);
      font-size: 13px;
      letter-spacing: 0.01em;
    }

    .topbar-inner {
      width: min(1200px, calc(100% - 40px));
      margin: 0 auto;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 16px;
      padding: 9px 0;
      flex-wrap: wrap;
    }

    .topbar a {
      color: rgba(255, 255, 255, 0.9);
      transition: color 0.2s;
    }

    .topbar a:hover {
      color: var(--brand);
      text-decoration:none;
    }

    .nav-wrap {
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
      background: rgba(13, 31, 45, 0.96);
      position: sticky;
      top: 0;
      z-index: 100;
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      transition: box-shadow 0.3s;
    }

    .nav-wrap.scrolled {
      box-shadow: 0 4px 30px rgba(0, 0, 0, 0.25);
    }

    .nav {
      width: min(1200px, calc(100% - 40px));
      margin: 0 auto;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 24px;
      min-height: 72px;
    }

    .logo {
      font-family: 'Work Sans', sans-serif;
      font-size: 28px;
      font-weight: 800;
      color: #fff;
      letter-spacing: -0.03em;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .logo-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: var(--brand);
      display: inline-block;
    }

    .nav-links {
      display: flex;
      align-items: center;
      gap: 28px;
      font-size: 14px;
      font-weight: 500;
      color: rgba(255, 255, 255, 0.65);
    }

    .nav-links a {
      transition: color 0.2s;
      letter-spacing: 0.01em;
    }

    .nav-links a:hover {
      color: #fff;
    }

    .btn,
    button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      min-height: 48px;
      padding: 12px 22px;
      border-radius: 8px;
      border: none;
      background: var(--brand);
      color: #fff;
      font-family: 'DM Sans', sans-serif;
      font-weight: 600;
      font-size: 14.5px;
      cursor: pointer;
      letter-spacing: 0.01em;
      position: relative;
      overflow: hidden;
      transition: transform 0.2s, box-shadow 0.2s, background 0.2s;
    }

    .btn::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, transparent 60%);
      pointer-events: none;
    }

    .btn:hover,
    button:hover {
      background: var(--brand-dark);
      transform: translateY(-1px);
      box-shadow: 0 8px 24px rgba(10, 127, 145, 0.35);
      color:white;
    }

    .btn-ghost {
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      color: #fff;
      backdrop-filter: blur(8px);
    }

    .btn-ghost:hover {
      background: rgba(255, 255, 255, 0.18);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    }

    .btn-accent {
      background: var(--brand);
      color: #fff;
      font-weight: 700;
    }

    .btn-accent:hover {
      background: var(--brand-dark);
      box-shadow: 0 8px 24px rgba(10, 127, 145, 0.4);
    }

    .btn-nav {
      min-height: 38px;
      padding: 8px 18px;
      font-size: 13.5px;
      background: var(--brand);
      color: #fff;
      font-weight: 700;
      margin-top:0;
      text-transform:'
    }

    .btn-nav:hover {
      background: var(--brand-dark);
      box-shadow: 0 6px 18px rgba(10, 127, 145, 0.4);
    }

    .hero {
      position: relative;
      background: var(--navy);
      color: #fff;
      overflow: hidden;
      padding: 0 !important;
    }

    #hero-h1 {
    color:White;}

    .hero-bg {
      position: absolute;
      inset: 0;
      background: url("https://www.diviv.com.au/wp-content/uploads/2020/06/devices.jpg") center/cover;
      opacity: 0.12;
    }

    .hero-gradient {
      position: absolute;
      inset: 0;
      background:
        radial-gradient(ellipse 80% 70% at 60% 40%, rgba(10, 127, 145, 0.25) 0%, transparent 60%),
        radial-gradient(ellipse 50% 80% at 10% 80%, rgba(244, 181, 68, 0.08) 0%, transparent 50%),
        linear-gradient(160deg, rgba(13, 31, 45, 0.6) 0%, rgba(13, 31, 45, 0.95) 100%);
    }

    /* Animated grid lines */
    .hero-grid {
      position: absolute;
      inset: 0;
      background-image:
        linear-gradient(rgba(10, 127, 145, 0.06) 1px, transparent 1px),
        linear-gradient(90deg, rgba(10, 127, 145, 0.06) 1px, transparent 1px);
      background-size: 60px 60px;
      mask-image: radial-gradient(ellipse 80% 80% at 50% 0%, black 30%, transparent 100%);
    }

    /* Floating orbs */
    .hero-orb {
      position: absolute;
      border-radius: 50%;
      filter: blur(80px);
      opacity: 0.18;
      pointer-events: none;
    }

    .hero-orb-1 {
      width: 600px;
      height: 600px;
      background: var(--brand);
      top: -200px;
      right: -100px;
    }

    .hero-orb-2 {
      width: 400px;
      height: 400px;
      background: var(--brand);
      bottom: -100px;
      left: 20%;
      opacity: 0.1;
    }

    .hero-inner {
      position: relative;
      z-index: 2;
      width: min(1200px, calc(100% - 40px));
      margin: 0 auto;
      display: grid;
      grid-template-columns: minmax(0, 1.1fr) minmax(300px, 0.65fr);
      gap: 60px;
      min-height: clamp(680px, calc(100vh - 130px), 860px);
      padding: 80px 0;
    }

    .eyebrow {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 20px;
      color: var(--brand);
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.12em;
    }

    .eyebrow::before {
      content: '';
      width: 24px;
      height: 2px;
      background: var(--brand);
      display: inline-block;
    }

    h1 {
      font-family: 'Work Sans', sans-serif;
      font-size: clamp(42px, 6vw, 72px);
      font-weight: 800;
      line-height: 0.95;
      letter-spacing: -0.03em;
      margin-bottom: 24px;
      max-width: 800px;
    }

    h1 em {
      font-style: normal;
      color: var(--brand);
    }

    .hero-copy {
      font-size: 18px;
      color: rgba(255, 255, 255, 0.72);
      max-width: 580px;
      margin-bottom: 36px;
      line-height: 1.65;
      font-weight: 300;
    }

    .hero-actions {
      display: flex;
      gap: 12px;
      flex-wrap: wrap;
      margin-bottom: 44px;
    }

    /* Proof row */
    .proof-row {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 12px;
      max-width: 620px;
    }

    .proof-item {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: var(--radius);
      padding: 16px;
      backdrop-filter: blur(8px);
      transition: background 0.3s, border-color 0.3s;
    }

    .proof-item:hover {
      background: rgba(255, 255, 255, 0.09);
      border-color: rgba(10, 127, 145, 0.4);
    }

    .proof-item strong {
      display: block;
      font-family: 'Work Sans', sans-serif;
      font-size: 20px;
      font-weight: 700;
      line-height: 1.1;
      margin-bottom: 5px;
      color: #fff;
    }

    .proof-item span {
      color: rgba(255, 255, 255, 0.55);
      font-size: 13px;
      line-height: 1.4;
    }

    .lead-form {
      background: #fff;
      color: var(--ink);
      border-radius: var(--radius-lg);
      box-shadow: var(--shadow-lg);
      padding: 32px 28px;
      position: relative;
      overflow: hidden;
    }

    .lead-form::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: linear-gradient(90deg, var(--brand) 0%, var(--brand) 100%);
    }

    .lead-form h2 {
      font-family: 'Work Sans', sans-serif;
      font-size: 22px;
      font-weight: 700;
      line-height: 1.2;
      margin-bottom: 6px;
      letter-spacing: -0.02em;
    }

    .lead-form p {
      color: var(--muted);
      font-size: 14px;
      margin-bottom: 20px;
    }

    label {
      display: block;
      font-weight: 600;
      font-size: 12.5px;
      margin-bottom: 5px;
      letter-spacing: 0.02em;
      color: var(--ink);
    }

    input,
    select,
    textarea {
      width: 100%;
      border: 1.5px solid var(--line);
      border-radius: 8px;
      min-height: 44px;
      padding: 10px 13px;
      font: inherit;
      font-size: 14px;
      color: var(--ink);
      background: var(--soft);
      transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
      outline: none;
    }

    input:focus,
    select:focus,
    textarea:focus {
      border-color: var(--brand);
      background: #fff;
      box-shadow: 0 0 0 3px rgba(10, 127, 145, 0.12);
    }

    textarea {
      min-height: 90px;
      resize: vertical;
    }

    .field {
      margin-bottom: 13px;
    }

    .thankyou-message {
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }

    .form-note {
      font-size: 12.5px;
      color: var(--muted);
      margin: 12px 0 0;
      text-align: center;
      line-height: 1.5;
    }

    button[type="submit"] {
      width: 100%;
      background: var(--brand);
      font-size: 15px;
      font-weight: 700;
      min-height: 50px;
    }

    .section-inner {
      width: min(1200px, calc(100% - 40px));
      margin: 0 auto;
    }

    section {
      padding: 90px 0;
    }

    .section-kicker {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      color: var(--brand);
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      margin-bottom: 14px;
    }

    .section-kicker::before {
      content: '';
      width: 20px;
      height: 2px;
      background: var(--brand);
      display: inline-block;
    }

    h2 {
      font-family: 'Work Sans', sans-serif;
      font-size: clamp(30px, 4vw, 46px);
      font-weight: 800;
      line-height: 1.05;
      margin-bottom: 16px;
      letter-spacing: -0.03em;
    }

    h3 {
      font-family: 'Work Sans', sans-serif;
      font-weight: 700;
    }

    .section-lead {
      font-size: 18px;
      color: var(--muted);
      max-width: 760px;
      margin-bottom: 48px;
      line-height: 1.7;
      font-weight: 300;
    }

    .logo-strip {
      background: var(--soft);
      border-top: 1px solid var(--line);
      border-bottom: 1px solid var(--line);
      padding: 28px 0;
      overflow: hidden;
    }

    .logo-strip .section-inner {
      display: flex;
      align-items: center;
      gap: 24px;
      flex-wrap: wrap;
    }

    .logo-strip-label {
      color: var(--muted);
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      white-space: nowrap;
    }

    .client-list {
      display: flex;
      gap: 0;
      flex-wrap: nowrap;
      overflow: hidden;
      flex: 1;
    }

    .client-track {
      display: flex;
      gap: 48px;
      align-items: center;
      animation: marquee 20s linear infinite;
      flex-shrink: 0;
    }

    @keyframes marquee {
      from {
        transform: translateX(0);
      }

      to {
        transform: translateX(-50%);
      }
    }

    .client-track span {
      font-family: 'Work Sans', sans-serif;
      font-weight: 700;
      font-size: 15px;
      color: #8a9aa7;
      white-space: nowrap;
      letter-spacing: -0.01em;
      transition: color 0.3s;
    }

    .grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 20px;
    }

    .support-card {
      background: #fff;
      border: 1px solid var(--line);
      border-radius: var(--radius-lg);
      padding: 28px;
      position: relative;
      overflow: hidden;
      transition: transform 0.3s, box-shadow 0.3s, border-color 0.3s;
    }

    .support-card::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 2px;
      background: linear-gradient(90deg, var(--brand), var(--brand));
      transform: scaleX(0);
      transform-origin: left;
      transition: transform 0.35s ease;
    }

    .support-card:hover {
      transform: translateY(-4px);
      box-shadow: var(--shadow);
      border-color: rgba(10, 127, 145, 0.2);
    }

    .support-card:hover::before {
      transform: scaleX(1);
    }

    .card-icon {
      width: 44px;
      height: 44px;
      border-radius: 10px;
      background: var(--brand-light);
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 18px;
      color: var(--brand-dark);
    }

    .support-card h3 {
      font-size: 18px;
      line-height: 1.25;
      margin-bottom: 10px;
      letter-spacing: -0.02em;
    }

    .support-card p {
      color: var(--muted);
      font-size: 14.5px;
      line-height: 1.65;
    }

    .pale {
      background: var(--soft);
    }

    .two-col {
      display: grid;
      grid-template-columns: minmax(0, 0.85fr) minmax(0, 1fr);
      gap: 56px;
      align-items: center;
    }

    .checklist {
      list-style: none;
      padding: 0;
      display: grid;
      gap: 16px;
    }

    .checklist li {
      display: flex;
      gap: 14px;
      align-items: flex-start;
      color: var(--muted);
      font-size: 15px;
      line-height: 1.6;
    }

    .check-icon {
      width: 22px;
      height: 22px;
      border-radius: 50%;
      background: var(--success);
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-top: 1px;
    }

    .check-icon svg {
      width: 12px;
      height: 12px;
      color: #fff;
    }

    .checklist li strong {
      color: var(--ink);
      font-weight: 600;
    }

    .quote {
      background: linear-gradient(135deg, var(--navy) 0%, #1a3a50 100%);
      color: #fff;
      border-radius: var(--radius-lg);
      padding: 36px;
      position: relative;
      overflow: hidden;
    }

    .quote::before {
      content: '"';
      font-family: 'Work Sans', sans-serif;
      position: absolute;
      top: -20px;
      left: 24px;
      font-size: 160px;
      font-weight: 800;
      color: rgba(10, 127, 145, 0.2);
      line-height: 1;
      pointer-events: none;
    }

    .quote p {
      font-family: 'Work Sans', sans-serif;
      font-size: 22px;
      font-weight: 600;
      line-height: 1.4;
      color: rgba(255, 255, 255, 0.9);
      margin-bottom: 20px;
      position: relative;
      letter-spacing: -0.02em;
    }

    .quote strong {
      color: var(--brand);
      font-size: 13px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.08em;
    }

    .steps {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 20px;
      counter-reset: step;
    }

    .step {
      background: #fff;
      border-radius: var(--radius-lg);
      padding: 28px;
      counter-increment: step;
      position: relative;
      border: 1px solid var(--line);
      transition: transform 0.3s, box-shadow 0.3s;
    }

    .step:hover {
      transform: translateY(-3px);
      box-shadow: var(--shadow);
    }

    .step-num {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border-radius: 10px;
      background: var(--brand-light);
      color: var(--brand-dark);
      font-family: 'Work Sans', sans-serif;
      font-weight: 800;
      font-size: 15px;
      margin-bottom: 18px;
    }

    .step h3 {
      font-size: 17px;
      margin-bottom: 10px;
      letter-spacing: -0.02em;
    }

    .step p {
      color: var(--muted);
      font-size: 14.5px;
      line-height: 1.65;
    }

    /* Connector line */
    .steps-wrapper {
      position: relative;
    }

    .faq {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 20px 32px;
    }

    .faq-item {
      border-bottom: 1px solid var(--line);
      padding-bottom: 20px;
    }

    .faq-item h3 {
      font-size: 17px;
      margin-bottom: 8px;
      letter-spacing: -0.01em;
      line-height: 1.3;
    }

    .faq-item p {
      color: var(--muted);
      font-size: 14.5px;
      line-height: 1.7;
    }

    .testimonials {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 20px;
    }

    .testimonial {
      background: #fff;
      border-radius: var(--radius-lg);
      padding: 28px;
      border: 1px solid var(--line);
      position: relative;
      transition: transform 0.3s, box-shadow 0.3s;
    }

    .testimonial:hover {
      transform: translateY(-3px);
      box-shadow: var(--shadow);
    }

    .testimonial-quote {
      font-size: 32px;
      line-height: 1;
      color: var(--brand);
      font-family: 'Work Sans', sans-serif;
      font-weight: 800;
      margin-bottom: 10px;
    }

    .testimonial p {
      color: var(--ink);
      font-size: 16px;
      line-height: 1.55;
      margin-bottom: 18px;
      font-weight: 300;
      font-style: italic;
    }

    .testimonial-author {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .testimonial-avatar {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: linear-gradient(135deg, var(--brand-light), var(--brand));
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-family: 'Work Sans', sans-serif;
      font-weight: 700;
      font-size: 13px;
    }

    .testimonial span {
      display: block;
      color: var(--muted);
      font-size: 13px;
      font-weight: 600;
    }

    .final-cta {
      background: var(--navy);
      color: #fff;
      text-align: center;
      position: relative;
      overflow: hidden;
    }

    .final-cta::before {
      content: '';
      position: absolute;
      inset: 0;
      background:
        radial-gradient(ellipse 60% 80% at 50% 100%, rgba(10, 127, 145, 0.25) 0%, transparent 70%),
        radial-gradient(ellipse 40% 50% at 80% 20%, rgba(244, 181, 68, 0.08) 0%, transparent 60%);
    }

    .final-cta .section-inner {
      position: relative;
      z-index: 2;
    }

    .final-cta h2 {
      font-size: clamp(32px, 4.5vw, 52px);
      margin-bottom: 16px;
      max-width: 700px;
      margin-left: auto;
      margin-right: auto;
    }

    .final-cta p {
      color: rgba(255, 255, 255, 0.65);
      max-width: 600px;
      margin: 0 auto 32px;
      font-size: 18px;
      font-weight: 300;
    }

    footer {
      background: #081520;
      color: rgba(255, 255, 255, 0.45);
      padding: 32px 0;
      font-size: 13.5px;
    }

    footer .section-inner {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 20px;
      flex-wrap: wrap;
    }

    footer a {
      color: rgba(255, 255, 255, 0.65);
      transition: color 0.2s;
    }

    footer a:hover {
      color: var(--brand);
    }

    @media (max-width: 960px) {

      .hero-inner,
      .two-col {
        grid-template-columns: 1fr;
        min-height: auto;
      }

      .hero-inner {
        gap: 40px;
      }

      .grid,
      .steps,
      .testimonials {
        grid-template-columns: repeat(2, 1fr);
      }

      .proof-row {
        grid-template-columns: repeat(3, 1fr);
      }
    }

    @media (max-width: 720px) {
      .faq {
        grid-template-columns: 1fr;
      }

      .nav-links {
        display: none;
      }
    }

    @media (max-width: 580px) {

      .grid,
      .steps,
      .testimonials,
      .faq {
        grid-template-columns: 1fr;
      }

      .proof-row {
        grid-template-columns: 1fr;
        gap: 10px;
      }

      section {
        padding: 64px 0;
      }

      h1 {
        font-size: 40px;
      }

      .hero-actions .btn {
        width: 100%;
        justify-content: center;
      }

      .topbar-inner {
        flex-direction: column;
        gap: 4px;
      }
    }
        `}</style>
      </Helmet>
      <div className="google-ads-landing-page">
        <header>
          <div className="topbar">
            <div className="topbar-inner">
              <span>
                Perth-based web development, support &amp; maintenance
              </span>
              <span>Call <a href="tel:1300875978">1300 875 978</a> &nbsp;·&nbsp; <a href="mailto:hello@diviv.com.au">hello@diviv.com.au</a></span>
            </div>
          </div>
          <div className="nav-wrap" id="navbar">
            <nav className="nav" aria-label="Main navigation">
              <a className="logo" href="https://www.diviv.com.au/">
                <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 145.7 98.7" preserveAspectRatio="xMidYMid slice" xmlSpace="preserve" fill="#fff" className="" style={{ height: "37px", width: "53px" }}>
                  <path d="M91.2,84.5c-0.8-0.2-1.5-0.5-2.2-0.8c-0.8-0.4-1.6-0.9-2.4-1.5c-0.9-0.7-1.7-1.4-2.5-2.2l-1.1-1.1l-0.7-0.7l0.7-0.7l0.6-0.6 l2-2l2-2l2-2c1.3-1.3,2.6-2.7,3.7-4.2c1.1-1.4,2-2.9,2.8-4.5c0.8-1.5,1.4-3.1,1.8-4.8c0.4-1.6,0.7-3.3,0.7-5c0-1.4-0.2-2.7-0.4-4.1 c-0.3-1.4-0.8-2.8-1.4-4.1c-0.6-1.4-1.4-2.7-2.3-4c-1.1-1.4-2.2-2.7-3.5-3.8l-2.4-2.4l-5.8-5.8l-0.7-0.7l0.7-0.7l1.2-1.2l6.2-6.2 l0.7-0.7l0.7,0.7l7.9,7.9l9.3,9.3l9.3,9.3L126,54l0.7,0.7l-0.7,0.7l-5.5,5.5l-6.9,6.9l-6.9,6.9l-6.9,6.9c-0.5,0.5-1,1-1.6,1.4 c-0.5,0.4-1.1,0.7-1.6,1c-0.5,0.3-1.1,0.4-1.6,0.6c-0.6,0.1-1.1,0.2-1.7,0.2C92.5,84.8,91.8,84.7,91.2,84.5 M90.4,0.7l-4.6,4.6 l-9.2,9.2l-3.2,3.2l-0.7,0.7l-0.7-0.7l-3.2-3.2l-9.2-9.2l-4.6-4.6L54.5,0l-0.7,0.7L0.7,53.8L0,54.5l0.7,0.7l9,9l17.9,17.9l9,9 c1.2,1.2,2.5,2.3,3.9,3.3c1.3,0.9,2.6,1.7,4,2.3c1.3,0.6,2.7,1.1,4.1,1.4c1.3,0.3,2.7,0.5,4.1,0.4c1.7,0,3.4-0.2,5-0.7 c1.6-0.4,3.2-1.1,4.8-1.8c1.6-0.8,3.1-1.8,4.5-2.8l0.1-0.1l-9.8-9.8l-0.6,0.3c-0.7,0.4-1.4,0.6-2.2,0.8c-0.7,0.2-1.3,0.3-2,0.3 c-0.6,0-1.1-0.1-1.7-0.2c-0.6-0.2-1.2-0.4-1.7-0.7c-0.6-0.3-1.1-0.6-1.6-1c-0.6-0.4-1.1-0.9-1.6-1.4L39,74.8l-6.9-6.9l-6.9-6.9 l-5.6-5.5l-0.7-0.7l0.7-0.7l7.9-7.9l9.3-9.3l9.3-9.3l7.9-7.9l0.7-0.7l0.7,0.7l1.2,1.2l5.1,5.1l2.5,2.5l8,8l2.4,2.4l4.8,4.8l2.4,2.4 c0.5,0.5,1,1,1.4,1.6c0.4,0.5,0.7,1,1,1.6c0.2,0.5,0.4,1.1,0.6,1.6c0.1,0.5,0.2,1.1,0.2,1.7c0,0.7-0.1,1.3-0.3,2 c-0.2,0.8-0.5,1.5-0.8,2.2c-0.4,0.8-1,1.6-1.6,2.4c-0.7,0.9-1.4,1.7-2.2,2.5l-3.9,3.9L73.5,68l-0.7,0.7L72.1,68l-2.6-2.6l-2-2l-2-2 c-0.8-0.8-1.5-1.6-2.1-2.5c-0.6-0.8-1-1.6-1.5-2.4c-0.4-0.7-0.6-1.4-0.8-2.2c-0.2-0.7-0.3-1.3-0.3-2c0-1.1,0.3-2.3,0.8-3.3 c0.3-0.6,0.6-1.1,1-1.6c0.4-0.6,0.9-1.1,1.3-1.6l2.4-2.4l0.7-0.7l-0.7-0.7l-8-8l-0.7-0.7L57,34.1l-2.4,2.4c-1.2,1.2-2.3,2.5-3.3,3.9 c-0.9,1.3-1.7,2.6-2.3,4c-0.6,1.3-1.1,2.7-1.4,4.1c-0.3,1.3-0.4,2.7-0.5,4c0,1.7,0.2,3.4,0.7,5c0.4,1.7,1,3.3,1.8,4.8 c0.8,1.6,1.8,3.1,2.8,4.5c1.1,1.5,2.4,2.9,3.7,4.2l2,2l2,2l2,2l2,2l8.8,8.8l0.6,0.6l0.6,0.6l0.6,0.6c1.3,1.3,2.7,2.6,4.2,3.7 c1.4,1.1,2.9,2,4.5,2.8c1.5,0.8,3.1,1.4,4.8,1.8c1.6,0.4,3.3,0.6,5,0.6c1.4,0,2.7-0.2,4.1-0.4c1.4-0.3,2.7-0.8,4-1.4 c1.4-0.7,2.7-1.4,4-2.3c1.4-1.1,2.7-2.2,3.8-3.5l9-9L145,55.1l0.7-0.7l-0.7-0.7l-53.1-53L91.2,0L90.4,0.7z"></path>
                </svg>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 277.99 98.35" className="logo-text" style={{ width: "65px" }}>
                  <path d="M72.85,10.84V.26H69.34c-3.62,0-9.62,4.34-9.62,11.88V32.55h0v3.27l-3.07-2.51a33,33,0,0,0-21.42-7.46,33.46,33.46,0,0,0-25,10.82A36.52,36.52,0,0,0,62.19,88,35.22,35.22,0,0,0,72.85,62.31Zm-20,67.89A22.53,22.53,0,0,1,36.4,85.57a22.28,22.28,0,0,1-16.33-6.84,22.26,22.26,0,0,1-6.89-16.46,22.12,22.12,0,0,1,6.89-16.4,23,23,0,0,1,32.53-.29l.29.29a22.12,22.12,0,0,1,6.89,16.4,22.26,22.26,0,0,1-6.89,16.46" transform="translate(0 -0.26)" fill="#ffffff"></path>
                  <path d="M191.16,97.55c3.61,0,9.62-4.34,9.62-11.88V27h-3.52c-3.61,0-9.62,4.34-9.62,11.88V97.55Z" transform="translate(0 -0.26)" fill="#ffffff"></path>
                  <path d="M92.52,97.55c3.61,0,9.62-4.34,9.62-11.88V27H98.62C95,27,89,31.34,89,38.88V97.55Z" transform="translate(0 -0.26)" fill="#ffffff"></path>
                  <path d="M268.15,27a4.91,4.91,0,0,0-1.66.29,7,7,0,0,0-1.6.79,6.42,6.42,0,0,0-1.34,1.15,8.83,8.83,0,0,0-1,1.44l-2.06,5.93-1.65,4.72-1.22,3.41-.06.15-.12.31-.6,1.56-1.95,5-1,2.55-1,2.54-1,2.54-1,2.55L248,69.29,246.7,73l-1.43,3.68-.44,1.15L244,80.11l-.45,1.15-.43-1.16-1.33-3.43-2.4-6.22L234.52,58l-2.45-6.23-2-5-.62-1.61-.09-.26-.06-.12-1.17-3.31-1.74-4.91-2.15-5.94a4.69,4.69,0,0,0-.87-1.37A6.63,6.63,0,0,0,222,28.1a7.44,7.44,0,0,0-1.6-.79,5.21,5.21,0,0,0-1.57-.31h-9.8l.23.6.23.59.23.6.23.6.23.61.24.6.23.61.23.61,2.68,7,2.67,6.93,16.15,43.92a11.79,11.79,0,0,0,22.26,0l16.15-44,1.06-2.74,1.74-4.52,1.74-4.52,1.7-4.52.24-.59.23-.6.22-.6L278,27Z" transform="translate(0 -0.26)" fill="#ffffff"></path>
                  <path d="M169.5,27a5.28,5.28,0,0,0-1.67.29,7.37,7.37,0,0,0-1.59.79,6.42,6.42,0,0,0-1.34,1.15A5,5,0,0,0,164,30.6l-2,5-1.94,5-1.17,3a.23.23,0,0,1-.06.15l-.12.31-.6,1.55-1.94,5-1,2.54-1,2.55-1,2.54-1,2.54-2.85,7.37L148.07,72l-1.43,3.68-.44,1.14-.88,2.29-.44,1.14-.45-1.14-1.36-3.48-2.4-6.22L135.87,57l-2.41-6.22-2-5-.62-1.61-.09-.25-.06-.13-1.17-3-1.95-5-1.94-5a4.87,4.87,0,0,0-.86-1.37,6.68,6.68,0,0,0-1.34-1.15,7,7,0,0,0-1.6-.79,4.91,4.91,0,0,0-1.66-.29h-9.84l.23.6.23.59.23.6.23.6.24.61.23.6.23.61.23.61,2.69,7,2.67,6.93,16.14,43.92a11.79,11.79,0,0,0,22.26,0l16.15-44,1.06-2.74,1.74-4.52,1.74-4.52,1.74-4.52.22-.59.24-.6.22-.6.23-.59Z" transform="translate(0 -0.26)" fill="#ffffff"></path>
                </svg>
              </a>
              <div className="nav-links">
                <a href="#help">
                  What we fix
                </a>
                <a href="#process">
                  How it works
                </a>
                <a href="#faq">
                  FAQ
                </a>
                <a className="btn btn-nav" href="#review">
                  Book a free review
                </a>
              </div>
            </nav>
          </div>
        </header>
        <main>
          {/* HERO */}
          <section className="hero" id="review">
            <div className="hero-bg"></div>
            <div className="hero-gradient"></div>
            <div className="hero-grid"></div>
            <div className="hero-orb hero-orb-1"></div>
            <div className="hero-orb hero-orb-2"></div>
            <div className="hero-inner">
              <div>
                <p className="eyebrow" id="hero-eyebrow">
                  Website support review — Perth businesses
                </p>
                <h1 id="hero-h1">
                  Unhappy With
                  <br />
                  Your Current
                  <br />
                  <em>
                    Web Developer?
                  </em>
                </h1>
                <p className="hero-copy" id="hero-copy">
                  If your website, CMS or custom application has become slow, unreliable or hard to change — Diviv can help you get control back with practical development support from an experienced Perth team.
                </p>
                <div className="hero-actions" id="hero-actions">
                  <a className="btn btn-accent" href="#review">
                    Book a free review →
                  </a>
                  <a className="btn btn-ghost" href="tel:1300875978">
                    <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                    </svg>
                    1300 875 978
                  </a>
                </div>
                <div className="proof-row" id="hero-proof" aria-label="Diviv proof points">
                  <div className="proof-item">
                    <strong>
                      15+
                    </strong>
                    <span>
                      years delivering web &amp; app solutions
                    </span>
                  </div>
                  <div className="proof-item">
                    <strong>
                      Perth
                    </strong>
                    <span>
                      local team, enterprise experience
                    </span>
                  </div>
                  <div className="proof-item">
                    <strong>
                      CMS + Apps
                    </strong>
                    <span>
                      websites, platforms &amp; integrations
                    </span>
                  </div>
                </div>
              </div>
              <form
                className="lead-form"
                onSubmit={formik.handleSubmit}
                data-netlify="true"
                netlify-honeypot="bot-field"
                name="work-with-us"
              >
                <input type="hidden" name="form-name" value="work-with-us" />
                <p className="hidden" hidden>
                  <label>
                    Don't fill this out if you're human:
                    <input
                      name="bot-field"
                      value={formik.values["bot-field"]}
                      onChange={formik.handleChange}
                    />
                  </label>
                </p>
                {formSubmitted ? (
                  <div className="thankyou-message">
                    <h2>Thanks for contacting us</h2>
                    <p className="form-note">We'll be in touch soon.</p>
                  </div>
                ) : (
                  <>
                    <h2>
                      Book a free website
                      <br />
                      support review
                    </h2>
                    <p>
                      Tell us what is not working. We'll review the situation and suggest the clearest next step.
                    </p>
                    <div className="field">
                      <label htmlFor="name">Name</label>
                      <input
                        id="name"
                        name="full_name"
                        autoComplete="name"
                        placeholder="Jane Smith"
                        value={formik.values.full_name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={
                          formik.errors.full_name && formik.touched.full_name
                            ? "is-invalid"
                            : ""
                        }
                      />
                      <div className="invalid-feedback">{formik.errors.full_name}</div>
                    </div>
                    <div className="field">
                      <label htmlFor="email">Work email</label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        placeholder="jane@company.com.au"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={
                          formik.errors.email && formik.touched.email
                            ? "is-invalid"
                            : ""
                        }
                      />
                      <div className="invalid-feedback">{formik.errors.email}</div>
                    </div>
                    <div className="field">
                      <label htmlFor="phone">Phone</label>
                      <input
                        id="phone"
                        name="mobilephone"
                        autoComplete="tel"
                        placeholder="(08) 1234 5678"
                        value={formik.values.mobilephone}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={
                          formik.errors.mobilephone && formik.touched.mobilephone
                            ? "is-invalid"
                            : ""
                        }
                      />
                      <div className="invalid-feedback">{formik.errors.mobilephone}</div>
                    </div>
                    <div className="field">
                      <label htmlFor="need">What do you need help with?</label>
                      <select
                        id="need"
                        name="need"
                        value={formik.values.need}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={
                          formik.errors.need && formik.touched.need
                            ? "is-invalid"
                            : ""
                        }
                      >
                        <option value="Website maintenance and support">
                          Website maintenance and support
                        </option>
                        <option value="CMS support or migration">
                          CMS support or migration
                        </option>
                        <option value="Custom web application">
                          Custom web application
                        </option>
                        <option value="Business system integration">
                          Business system integration
                        </option>
                        <option value="Not sure yet">Not sure yet</option>
                      </select>
                      <div className="invalid-feedback">{formik.errors.need}</div>
                    </div>
                    <div className="field">
                      <label htmlFor="message">What is happening?</label>
                      <textarea
                        id="message"
                        name="message"
                        placeholder="e.g. our current provider is slow to respond, the CMS is hard to update, or we need help finishing a project."
                        value={formik.values.message}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={
                          formik.errors.message && formik.touched.message
                            ? "is-invalid"
                            : ""
                        }
                      ></textarea>
                      <div className="invalid-feedback">{formik.errors.message}</div>
                    </div>
                    <button type="submit" disabled={formik.isSubmitting}>
                      {formik.isSubmitting ? "Sending..." : "Request my free review →"}
                    </button>
                    <p className="form-note">
                      No pressure. Just a practical conversation about what matters and what to do next.
                    </p>
                    <div
                      className={
                        "invalid-feedback " + (formik.errors.api ? "d-block" : "")
                      }
                    >
                      {formik.errors.api}
                    </div>
                  </>
                )}
              </form>
            </div>
          </section>
          {/* CLIENT STRIP */}
          <div className="logo-strip">
            <div className="section-inner">
              <span className="logo-strip-label">
                Project experience includes
              </span>
              <div className="client-list" aria-label="Selected Diviv clients">
                <div className="client-track">
                  <span>
                    St John Ambulance
                  </span>
                  <span>
                    Murdoch University
                  </span>
                  <span>
                    Wesfarmers
                  </span>
                  <span>
                    Pharmacy777
                  </span>
                  <span>
                    Energy Action
                  </span>
                  <span>
                    St John Ambulance
                  </span>
                  <span>
                    Murdoch University
                  </span>
                  <span>
                    Wesfarmers
                  </span>
                  <span>
                    Pharmacy777
                  </span>
                  <span>
                    Energy Action
                  </span>
                </div>
              </div>
            </div>
          </div>
          {/* SERVICES */}
          <section id="help">
            <div className="section-inner">
              <p className="section-kicker">
                What we can help with
              </p>
              <h2>
                Support for websites that need
                <br />
                more than a quick patch.
              </h2>
              <p className="section-lead">
                Diviv helps businesses stabilise, maintain and improve the digital systems they rely on. We're a strong fit when you need a development partner who can understand the existing build, explain your options clearly, and keep momentum moving.
              </p>
              <div className="grid">
                <article className="support-card">
                  <div className="card-icon">
                    <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                    </svg>
                  </div>
                  <h3>
                    Website maintenance &amp; support
                  </h3>
                  <p>
                    Keep your website secure, updated and working properly with responsive technical support and ongoing improvement.
                  </p>
                </article>
                <article className="support-card">
                  <div className="card-icon">
                    <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"></path>
                    </svg>
                  </div>
                  <h3>
                    CMS support &amp; migrations
                  </h3>
                  <p>
                    Get help with Sitefinity, Umbraco, Drupal, WordPress, Kentico migrations and CMS issues blocking your team.
                  </p>
                </article>
                <article className="support-card">
                  <div className="card-icon">
                    <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                    </svg>
                  </div>
                  <h3>
                    Rescue &amp; takeover projects
                  </h3>
                  <p>
                    If a project has stalled or your current provider isn't delivering, we assess the build and create a practical recovery plan.
                  </p>
                </article>
                <article className="support-card">
                  <div className="card-icon">
                    <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"></path>
                    </svg>
                  </div>
                  <h3>
                    Custom web applications
                  </h3>
                  <p>
                    Design and build business tools, portals and workflows that support how your team and customers actually work.
                  </p>
                </article>
                <article className="support-card">
                  <div className="card-icon">
                    <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                    </svg>
                  </div>
                  <h3>
                    Business system integration
                  </h3>
                  <p>
                    Connect websites, platforms and internal systems so data moves cleanly and business processes run with less friction.
                  </p>
                </article>
                <article className="support-card">
                  <div className="card-icon">
                    <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                    </svg>
                  </div>
                  <h3>
                    UX &amp; technical consultancy
                  </h3>
                  <p>
                    Improve usability, performance and product direction with evidence-based advice before committing to major development.
                  </p>
                </article>
              </div>
            </div>
          </section>
          {/* WHY DIVIV */}
          <section className="pale">
            <div className="section-inner two-col">
              <div>
                <p className="section-kicker">
                  Why businesses come to Diviv
                </p>
                <h2>
                  When support is inconsistent, small issues become expensive.
                </h2>
                <p className="section-lead" style={{ marginBottom: "0" }}>
                  Slow responses, unclear estimates and fragile fixes make it hard to plan. Diviv gives you a clearer path forward — whether you need short-term rescue work or a long-term technical partner.
                </p>
              </div>
              <div>
                <ul className="checklist">
                  <li>
                    <span className="check-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </span>
                    <span>
                      <strong>
                        Your provider is slow to respond.
                      </strong>
                      We help define what needs attention first and create a support rhythm that suits your business.
                    </span>
                  </li>
                  <li>
                    <span className="check-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </span>
                    <span>
                      <strong>
                        Your CMS is hard to use or maintain.
                      </strong>
                      We can improve, support or migrate your current platform.
                    </span>
                  </li>
                  <li>
                    <span className="check-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </span>
                    <span>
                      <strong>
                        Your website has become risky to change.
                      </strong>
                      We review the existing build before recommending fixes.
                    </span>
                  </li>
                  <li>
                    <span className="check-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </span>
                    <span>
                      <strong>
                        You need more than a brochure site.
                      </strong>
                      We work across custom applications, integrations, portals and complex digital systems.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </section>
          {/* TRUST SECTION */}
          <section>
            <div className="section-inner two-col">
              <div className="quote">
                <p>
                  "We provide innovative solutions that simplify complex problems."
                </p>
                <strong>
                  — Diviv Group
                </strong>
              </div>
              <div>
                <p className="section-kicker">
                  Built for long-term trust
                </p>
                <h2>
                  A Perth development team for complex web problems.
                </h2>
                <p className="section-lead" style={{ marginBottom: "28px" }}>
                  Diviv works at the intersection of technology, creativity and business outcomes. Our team supports website development, application development, system integration, UX strategy and technical consultancy for organisations that need dependable digital delivery.
                </p>
                <a className="btn" href="#review">
                  Start with a free review →
                </a>
              </div>
            </div>
          </section>
          {/* HOW IT WORKS */}
          <section className="pale" id="process">
            <div className="section-inner">
              <p className="section-kicker">
                How it works
              </p>
              <h2>
                Start small. Get clarity.
                <br />
                Move with confidence.
              </h2>
              <div className="steps steps-wrapper">
                <article className="step">
                  <div className="step-num">
                    01
                  </div>
                  <h3>
                    Tell us what is not working
                  </h3>
                  <p>
                    Share the website, CMS, support or project issue you're dealing with. We'll ask the right questions and look at the practical context.
                  </p>
                </article>
                <article className="step">
                  <div className="step-num">
                    02
                  </div>
                  <h3>
                    We review the situation
                  </h3>
                  <p>
                    We identify the likely risks, blockers and opportunities, then explain your options in clear language without jargon.
                  </p>
                </article>
                <article className="step">
                  <div className="step-num">
                    03
                  </div>
                  <h3>
                    You choose the next step
                  </h3>
                  <p>
                    That may be a support plan, a rescue sprint, a migration, a UX review or a larger development roadmap. You decide.
                  </p>
                </article>
              </div>
            </div>
          </section>
          {/* TESTIMONIALS */}
          <section>
            <div className="section-inner">
              <p className="section-kicker">
                Proof from existing work
              </p>
              <h2>
                Trusted for corporate website support
                <br />
                and complex builds.
              </h2>
              <p className="section-lead">
                Diviv's project history includes enterprise CMS development, client portals, support retainers, migrations, internal news systems and custom application work.
              </p>
              <div className="testimonials">
                <article className="testimonial">
                  <div className="testimonial-quote">
                    "
                  </div>
                  <p>
                    Professional, responsive and proactive support for corporate website needs.
                  </p>
                  <div className="testimonial-author">
                    <div className="testimonial-avatar">
                      WC
                    </div>
                    <span>
                      Wesfarmers Corporate Affairs
                    </span>
                  </div>
                </article>
                <article className="testimonial">
                  <div className="testimonial-quote">
                    "
                  </div>
                  <p>
                    A solution-based partner for front-end and back-end website delivery.
                  </p>
                  <div className="testimonial-author">
                    <div className="testimonial-avatar">
                      BA
                    </div>
                    <span>
                      Senior Business Analyst
                    </span>
                  </div>
                </article>
                <article className="testimonial">
                  <div className="testimonial-quote">
                    "
                  </div>
                  <p>
                    Delivery support that helped a tight project reach go-live on time.
                  </p>
                  <div className="testimonial-author">
                    <div className="testimonial-avatar">
                      PM
                    </div>
                    <span>
                      Project Manager
                    </span>
                  </div>
                </article>
              </div>
            </div>
          </section>
          {/* FAQ */}
          <section className="pale" id="faq">
            <div className="section-inner">
              <p className="section-kicker">
                Common questions
              </p>
              <h2>
                Before you enquire
              </h2>
              <div className="faq">
                <article className="faq-item">
                  <h3>
                    Can you take over from another developer?
                  </h3>
                  <p>
                    Yes. We can review the current state of your website or application, identify what is needed, and recommend a sensible support or rescue plan.
                  </p>
                </article>
                <article className="faq-item">
                  <h3>
                    Do you only work with Perth businesses?
                  </h3>
                  <p>
                    We're Perth-based and work well with local businesses, but we can support organisations across Australia where the fit is right.
                  </p>
                </article>
                <article className="faq-item">
                  <h3>
                    What platforms do you support?
                  </h3>
                  <p>
                    Diviv has experience across CMS, web application and integration projects including Sitefinity, Umbraco, Drupal, Azure, Angular and .NET.
                  </p>
                </article>
                <article className="faq-item">
                  <h3>
                    Is the review really free?
                  </h3>
                  <p>
                    Yes. The first review is a low-pressure conversation to understand what is happening and whether Diviv is the right team to help.
                  </p>
                </article>
              </div>
            </div>
          </section>
          {/* FINAL CTA */}
          <section className="final-cta">
            <div className="section-inner" style={{ textAlign: "center" }}>
              <p className="section-kicker" style={{ justifyContent: "center", color: "var(--brand)" }}>
                Ready to move forward?
              </p>
              <h2 style={{ color: 'white' }}>
                Ready for better web
                <br />
                development support?
              </h2>
              <p>
                Book a free website support review and find out what it would take to stabilise, improve or move on from your current setup.
              </p>
              <a className="btn btn-accent" href="#review" style={{ fontSize: "16px", minHeight: "54px", padding: "14px 30px" }}>
                Book a free website support review →
              </a>
            </div>
          </section>
        </main>
        <footer>
          <div className="section-inner">
            <span>
              Diviv Group &nbsp;·&nbsp; 165 / 15 Aberdeen St, Perth WA 6000
            </span>
            <span>
              <a href="tel:1300875978">
                1300 875 978
              </a>
              &nbsp;|&nbsp;
              <a href="mailto:hello@diviv.com.au">
                hello@diviv.com.au
              </a>
            </span>
          </div>
        </footer>
      </div>
    </>
  );
}

export default WorkWithUs;
